import React, { Fragment, useMemo } from 'react';

import { shouldDisplayErrorMessages } from '@components/NextForms/common';
import { MobxZodField, MobxZodTypes } from '@monoid-dev/mobx-zod-form';
import { makeMuiStyles, useCommonStyles } from '@styles';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { uniq } from 'remeda';

const useStyles = makeMuiStyles((theme, common) => ({
  errorMessagesContainer: {
    ...common.textError,
    ...common.smallerText,
  },
}));

export type ErrorMessagesProps<
  T extends MobxZodTypes,
  Container extends keyof JSX.IntrinsicElements = 'div',
> = {
  container?: Container;
  field: MobxZodField<T>;
  containerProps?: React.ComponentProps<Container>;
  delimiter?: React.ReactNode;
};

export const ErrorMessages = observer(
  <T extends MobxZodTypes, Container extends keyof JSX.IntrinsicElements>(
    props: ErrorMessagesProps<T, Container>,
  ) => {
    const { container = 'div', field, containerProps, delimiter = <br /> } = props;

    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    const hasErrors = useMemo(
      () => shouldDisplayErrorMessages(field),
      [field.errorMessages.length, field.extraErrorMessages.length, field.touched],
    );

    const errorMessages = useMemo(
      () => (hasErrors ? uniq(field.errorMessages) : []),
      [hasErrors, field.errorMessages],
    );

    const extraErrorMessages = useMemo(
      () => (hasErrors ? uniq(field.extraErrorMessages) : []),
      [hasErrors, field.extraErrorMessages],
    );

    if (!hasErrors) {
      return null;
    }

    return React.createElement(
      container,
      { ...containerProps, className: clsx(containerProps, classes.errorMessagesContainer) },
      [...errorMessages, ...extraErrorMessages].map((e, i) => (
        <Fragment key={i}>
          {e}
          {delimiter}
        </Fragment>
      )) as React.ReactNode,
    );
  },
);
